import React, { useId } from "react";
import { motion } from "motion/react";
import * as stylex from "@stylexjs/stylex";
import { cn } from "@/lib/utils/index";
interface DotPatternProps extends React.SVGProps<SVGSVGElement> {
  width?: number;
  height?: number;
  x?: number;
  y?: number;
  cx?: number;
  cy?: number;
  cr?: number;
  className?: string;
  glow?: boolean;
  stylexStyle?: stylex.StyleXStyles;
}

/**
 * DotPattern component - renders a repeating dot pattern background.
 *
 * Performance optimization: When `glow=false` (default), uses a simple SVG <pattern>
 * element instead of rendering individual circles. This reduces DOM nodes from
 * potentially thousands (columns × rows) down to just one pattern definition.
 *
 * When `glow=true`, renders individual motion.circle elements for animation effects.
 * Use sparingly as this creates many DOM nodes.
 */
export function DotPattern({
  width = 16,
  height = 16,
  x = 0,
  y = 0,
  cx = 1,
  cy = 1,
  cr = 1,
  className,
  glow = false,
  stylexStyle,
  style,
  ...props
}: DotPatternProps) {
  const id = useId();

  // For non-glow mode, use efficient SVG pattern (single DOM element for entire pattern)
  if (!glow) {
    const stylexProps = stylex.props(stylexStyle);
    return <svg aria-hidden="true" className={cn("pointer-events-none absolute inset-0 h-full w-full text-neutral-400/80", className, stylexProps.className)} style={{
      ...stylexProps.style,
      ...style
    }} {...props}>
        <defs>
          <pattern id={`${id}-pattern`} width={width} height={height} patternUnits="userSpaceOnUse" x={x} y={y}>
            <circle cx={cx} cy={cy} r={cr} fill="currentColor" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${id}-pattern)`} />
      </svg>;
  }

  // Glow mode: render individual animated circles (use sparingly!)
  // Limit to a reasonable number of dots for performance
  return <GlowingDotPattern id={id} width={width} height={height} x={x} y={y} cx={cx} cy={cy} cr={cr} className={className} stylexStyle={stylexStyle} style={style} {...props} />;
}

/** Separate component for glow mode to isolate the heavier rendering logic */
function GlowingDotPattern({
  id,
  width,
  height,
  x,
  y,
  cx,
  cy,
  cr,
  className,
  stylexStyle,
  style,
  ...props
}: DotPatternProps & {
  id: string;
}) {
  const stylexProps = stylex.props(stylexStyle);
  const containerRef = React.useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = React.useState({
    width: 0,
    height: 0
  });
  React.useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const updateDimensions = () => {
      const {
        width,
        height
      } = element.getBoundingClientRect();
      setDimensions({
        width,
        height
      });
    };
    updateDimensions();
    const observer = new ResizeObserver(updateDimensions);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const dots = React.useMemo(() => {
    const cols = Math.ceil(dimensions.width / (width ?? 16));
    const rows = Math.ceil(dimensions.height / (height ?? 16));
    // Cap at 500 dots max to prevent performance issues
    const maxDots = 500;
    const count = Math.min(cols * rows, maxDots);
    return Array.from({
      length: count
    }, (_, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      return {
        x: col * (width ?? 16) + (cx ?? 1) + (x ?? 0),
        y: row * (height ?? 16) + (cy ?? 1) + (y ?? 0),
        delay: i % 17 * 0.18,
        duration: 2.2 + i % 9 * 0.16
      };
    });
  }, [cx, cy, dimensions.height, dimensions.width, height, width, x, y]);
  return <svg ref={containerRef} aria-hidden="true" className={cn("pointer-events-none absolute inset-0 h-full w-full text-neutral-400/80", className, stylexProps.className)} style={{
    ...stylexProps.style,
    ...style
  }} {...props}>
      <defs>
        <radialGradient id={`${id}-gradient`}>
          <stop offset="0%" stopColor="currentColor" stopOpacity="1" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </radialGradient>
      </defs>
      {dots.map(dot => <motion.circle key={`${dot.x}-${dot.y}`} cx={dot.x} cy={dot.y} r={cr} fill={`url(#${id}-gradient)`} initial={{
      opacity: 0.4,
      scale: 1
    }} animate={{
      opacity: [0.4, 1, 0.4],
      scale: [1, 1.5, 1]
    }} transition={{
      duration: dot.duration,
      repeat: Infinity,
      repeatType: "reverse",
      delay: dot.delay,
      ease: "easeInOut"
    }} />)}
    </svg>;
}